/**
 * Logo Chooser
 * Handles logo selection UI on product pages.
 *
 * Upload endpoint:
 *   Set window.LogoChooserUploadEndpoint to your upload API URL.
 *   Endpoint should accept multipart/form-data POST with a `file` field
 *   and return JSON: { url: "..." }
 *
 *   Until then, files are stored by filename — order team will see
 *   "FILE ATTACHED: filename.jpg" in the cart/order.
 */

class LogoChooser {
  constructor(el) {
    this.el = el;
    this.uploadCount    = parseInt(el.dataset.uploadCount, 10) || 1;
    this.uploadEndpoint = window.LogoChooserUploadEndpoint || '';
    this.sarrisImage    = el.dataset.sarrisImage || '';

    // Fee variant IDs resolved from SKUs in Liquid
    this.feeVariants = {
      new:      el.dataset.feeNew,
      existing: el.dataset.feeExisting,
      sarris:   el.dataset.feeSarris,
    };

    // UI elements
    this.radios         = el.querySelectorAll('.logo-chooser__radio');
    this.panels         = el.querySelectorAll('.logo-chooser__panel');
    this.fileInputs     = el.querySelectorAll('.logo-chooser__file-input');
    this.existingRadios = el.querySelectorAll('.logo-chooser__existing-radio');
    this.errorEl        = el.querySelector('.logo-chooser__error');

    // Cart property hidden inputs (on main product line item)
    this.propChoice   = el.querySelector('.lc-prop-choice');
    this.propType     = el.querySelector('.lc-prop-type');
    this.propFile1    = el.querySelector('.lc-prop-file-1');
    this.propFile2    = el.querySelector('.lc-prop-file-2');
    this.propSelected = el.querySelector('.lc-prop-selected');

    // Upload state per slot: { status, file, url }
    this.uploadState = {};

    // Standard imprint (coin_1_side Side Two)
    try { this.imprintImages = JSON.parse(el.dataset.imprintImages || 'null') || null; }
    catch (e) { this.imprintImages = null; }
    this.imprintSelects = el.querySelectorAll('.logo-chooser__imprint-select');
    this.propImprint    = el.querySelector('.lc-prop-imprint');

    // Product ID for post-add property sync
    this._productId  = el.closest('product-form-component')?.dataset.productId;
    this._feeUpdating = false;

    this._init();
  }

  _init() {
    // Logo choice radio buttons
    this.radios.forEach((radio) => {
      radio.addEventListener('change', () => this._onChoiceChange(radio.value));
    });

    // File inputs
    this.fileInputs.forEach((input) => {
      input.addEventListener('change', () => this._onFileSelected(input));
    });

    // Existing logo thumbnails
    this.existingRadios.forEach((radio) => {
      radio.addEventListener('change', () => this._onExistingSelected(radio));
    });

    // Standard imprint dropdown (coin_1_side Side Two)
    this.imprintSelects.forEach((select) => {
      select.addEventListener('change', () => this._onImprintChange(select));
    });

    // Intercept add-to-cart button click in capture phase for validation
    // This fires before Horizon's product-form-component handles it
    const form = this.el.closest('form[data-type="add-to-cart-form"]');
    if (form) {
      form.addEventListener('click', (e) => {
        const btn = e.target.closest('[name="add"], .add-to-cart-button');
        if (!btn) return;
        if (!this._validate()) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          // Remove any orphaned fee that was added when the radio was selected
          this._removeAllFeeItems().catch(() => {});
        }
      }, true); // capture phase — fires before component handler

      // Also intercept submit event as a fallback
      form.addEventListener('submit', (e) => {
        if (!this._validate()) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this._removeAllFeeItems().catch(() => {});
        }
      }, true);
    }

    // After cart update (fired by Horizon on any cart change), sync logo
    // properties onto the product line item — handles IO compatibility
    document.addEventListener('cart:update', () => this._syncPropertiesToCart());
  }

  // ----------------------------------------------------------------
  // Validation — returns true if ok, false + shows error if not
  // ----------------------------------------------------------------

  _validate() {
    const selected = this.el.querySelector('.logo-chooser__radio:checked')?.value;

    if (!selected) {
      this._showError('Please choose a logo option before adding to cart.');
      this.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    if (selected === 'new') {
      for (let slot = 1; slot <= this.uploadCount; slot++) {
        const state = this.uploadState[slot];
        if (!state?.file) {
          this._showError('Please select your logo file before adding to cart.');
          this.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }
        if (state.status === 'uploading') {
          this._showError('Your logo is still uploading. Please wait a moment.');
          this.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }
        if (state.status === 'error') {
          this._showError('Logo upload failed. Please try selecting your file again.');
          this.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }
      }
    }

    if (selected === 'existing') {
      const hasLogos = this.el.querySelectorAll('.logo-chooser__existing-radio').length > 0;
      if (!hasLogos) {
        this._showError('You don\'t have any logos on file yet. Please choose "New Logo Upload" instead.');
        this.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
      if (!this.propSelected?.value) {
        this._showError('Please select one of your existing logos.');
        this.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
    }

    this._clearError();
    return true;
  }

  // ----------------------------------------------------------------
  // Radio selection
  // ----------------------------------------------------------------

  _onChoiceChange(choice) {
    // Show/hide panels
    this.panels.forEach((p) => (p.hidden = true));
    const activePanel = this.el.querySelector(`.logo-chooser__panel--${choice}`);
    if (activePanel) activePanel.hidden = false;

    // Update main product hidden inputs
    if (this.propChoice) this.propChoice.value = this._choiceLabel(choice);
    if (this.propFile1)    this.propFile1.value    = '';
    if (this.propFile2)    this.propFile2.value    = '';
    if (this.propSelected) this.propSelected.value = '';

    this._clearError();

    // Update fee line item in cart
    this._updateFeeInCart(choice);
  }

  _choiceLabel(choice) {
    return {
      new:      'New Logo Upload',
      existing: 'Use Existing Logo',
      sarris:   'Use Sarris Logo',
    }[choice] || choice;
  }

  // ----------------------------------------------------------------
  // Cart fee management
  // ----------------------------------------------------------------

  async _updateFeeInCart(choice) {
    // Always remember the most recently requested choice. If an update is
    // already in flight, don't start a second overlapping request -- just
    // let the in-flight one pick up the latest choice when it finishes,
    // instead of silently dropping this call (which left a stale fee in
    // the cart when the customer switched selections quickly).
    this._pendingChoice = choice;
    if (this._feeUpdating) return;
    this._feeUpdating = true;

    try {
      while (this._pendingChoice !== undefined) {
        const current = this._pendingChoice;
        this._pendingChoice = undefined;

        await this._removeAllFeeItems();

        const variantId = this.feeVariants[current];
        if (!variantId || variantId === '0') continue;

        const properties = { 'Logo Type': this.el.dataset.logoType };

        if (current === 'sarris' && this.sarrisImage) {
          properties['Logo Preview'] = this.sarrisImage;
        }
        // Existing logo preview is set later in _onExistingSelected / _updateExistingFeePreview

        await this._cartAdd(variantId, 1, properties);
      }
    } catch (err) {
      console.warn('[LogoChooser] Cart fee update failed:', err);
    } finally {
      this._feeUpdating = false;
    }
  }

  async _removeAllFeeItems() {
    const cart = await this._getCart();
    const feeVariantIds = Object.values(this.feeVariants).filter((id) => id && id !== '0');
    const updates = {};
    cart.items.forEach((item) => {
      if (feeVariantIds.includes(String(item.variant_id))) {
        updates[item.key] = 0;
      }
    });
    if (Object.keys(updates).length > 0) {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
    }
  }

  async _cartAdd(variantId, quantity, properties = {}) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity, properties }] }),
    });
    if (!res.ok) throw new Error(`cart/add.js failed: ${res.status}`);
    return res.json();
  }

  async _getCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  // ----------------------------------------------------------------
  // Sync logo properties onto the product line item after cart update
  // This handles IO compatibility — IO submits its own payload, so we
  // patch our properties on afterwards via cart:update
  // ----------------------------------------------------------------

  async _syncPropertiesToCart() {
    const choice = this.el.querySelector('.logo-chooser__radio:checked')?.value;
    if (!choice || !this._productId) return;

    try {
      const cart = await this._getCart();
      const lineItem = cart.items.find(
        (item) => String(item.product_id) === String(this._productId)
      );

      if (!lineItem) {
        // Product not in cart — clean up any orphaned fee items
        await this._removeAllFeeItems();
        return;
      }

      // --- Sync logo properties onto the product line item (IO compatibility) ---
      const choiceLabel = this._choiceLabel(choice);
      if (lineItem.properties?.['Logo Choice'] !== choiceLabel) {
        const properties = {
          ...lineItem.properties,
          'Logo Choice': choiceLabel,
          'Logo Type':   this.propType?.value || '',
        };
        if (choice === 'new') {
          if (this.uploadState[1]?.file) {
            properties['Logo File 1'] = this.uploadState[1].url || `[pending: ${this.uploadState[1].file.name}]`;
          }
          if (this.uploadState[2]?.file) {
            properties['Logo File 2'] = this.uploadState[2].url || `[pending: ${this.uploadState[2].file.name}]`;
          }
        }
        if (this.propImprint?.value) {
          properties['Side Two Imprint'] = this.propImprint.value;
        }
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineItem.key, quantity: lineItem.quantity, properties }),
        });
      }

      // --- Ensure the correct fee is in cart alongside the product ---
      const correctFeeId = String(this.feeVariants[choice] || '');
      if (!correctFeeId || correctFeeId === '0') return;

      const allFeeIds   = Object.values(this.feeVariants).map(String).filter((id) => id && id !== '0');
      const correctInCart = cart.items.some((item) => String(item.variant_id) === correctFeeId);
      const wrongInCart   = cart.items.some((item) => allFeeIds.includes(String(item.variant_id)) && String(item.variant_id) !== correctFeeId);

      if (!correctInCart || wrongInCart) {
        await this._removeAllFeeItems();
        const feeProps = { 'Logo Type': this.el.dataset.logoType || '' };
        if (choice === 'sarris' && this.sarrisImage) {
          feeProps['Logo Preview'] = this.sarrisImage;
        }
        if (choice === 'existing' && this.propSelected?.value) {
          feeProps['Logo Preview'] = this.propSelected.value;
        }
        await this._cartAdd(correctFeeId, 1, feeProps);
      }
    } catch (err) {
      console.warn('[LogoChooser] Property sync failed:', err);
    }
  }

  // ----------------------------------------------------------------
  // File upload
  // ----------------------------------------------------------------

  _onFileSelected(input) {
    const slot      = parseInt(input.dataset.slot, 10) || 1;
    const file      = input.files[0];
    if (!file) return;

    const slotEl    = input.closest('.logo-chooser__upload-slot');
    const nameEl    = slotEl?.querySelector('.logo-chooser__file-name');
    const statusEl  = slotEl?.querySelector('.logo-chooser__upload-status');
    const previewEl = slotEl?.querySelector('.logo-chooser__file-preview');

    if (nameEl) nameEl.textContent = file.name;

    if (previewEl && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        previewEl.innerHTML = `<img src="${e.target.result}" alt="Preview">`;
        previewEl.hidden = false;
      };
      reader.readAsDataURL(file);
    }

    if (this.uploadEndpoint) {
      this._uploadFile(slot, file, statusEl);
    } else {
      this.uploadState[slot] = { status: 'pending', file, url: `[pending: ${file.name}]` };
      if (statusEl) {
        statusEl.textContent = `File selected: ${file.name}`;
        statusEl.className   = 'logo-chooser__upload-status logo-chooser__upload-status--pending';
      }
      this._syncFileProperties();
      this._updateFeeFileProperties();
    }
  }

  async _uploadFile(slot, file, statusEl) {
    this.uploadState[slot] = { status: 'uploading', file, url: '' };
    if (statusEl) {
      statusEl.textContent = 'Uploading…';
      statusEl.className   = 'logo-chooser__upload-status logo-chooser__upload-status--uploading';
    }
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('slot', slot);
      const res = await fetch(this.uploadEndpoint, { method: 'POST', body });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { url } = await res.json();
      this.uploadState[slot] = { status: 'uploaded', file, url };
      if (statusEl) {
        statusEl.textContent = '✓ Uploaded';
        statusEl.className   = 'logo-chooser__upload-status logo-chooser__upload-status--success';
      }
      this._syncFileProperties();
      this._updateFeeFileProperties();
    } catch (err) {
      this.uploadState[slot] = { status: 'error', file, url: '' };
      if (statusEl) {
        statusEl.textContent = 'Upload failed — please try again.';
        statusEl.className   = 'logo-chooser__upload-status logo-chooser__upload-status--error';
      }
    }
  }

  _syncFileProperties() {
    if (this.propFile1 && this.uploadState[1]) this.propFile1.value = this.uploadState[1].url;
    if (this.propFile2 && this.uploadState[2]) this.propFile2.value = this.uploadState[2].url;
  }

  _attachedFileName(slot) {
    const state = this.uploadState[slot];
    if (state?.url && !state.url.startsWith('[pending')) {
      try {
        const path = new URL(state.url).pathname;
        return decodeURIComponent(path.substring(path.lastIndexOf('/') + 1));
      } catch {
        // fall through to browser filename
      }
    }
    return state?.file?.name || '';
  }

  async _updateFeeFileProperties() {
    if (this._feeUpdating) return;
    const feeVariantId = this.feeVariants.new;
    if (!feeVariantId || feeVariantId === '0') return;
    try {
      const cart    = await this._getCart();
      const feeLine = cart.items.find((item) => String(item.variant_id) === String(feeVariantId));
      if (!feeLine) return;
      const properties = { ...feeLine.properties };
      if (this.uploadState[1]?.file) {
        properties['FILE ATTACHED'] = this._attachedFileName(1);
        if (this.uploadState[1].url && !this.uploadState[1].url.startsWith('[pending')) {
          properties['Logo Preview'] = this.uploadState[1].url;
        }
      }
      if (this.uploadState[2]?.file) {
        properties['FILE ATTACHED (Side 2)'] = this._attachedFileName(2);
      }
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: feeLine.key, quantity: 1, properties }),
      });
    } catch (err) {
      console.warn('[LogoChooser] Fee file property update failed:', err);
    }
  }

  // ----------------------------------------------------------------
  // Standard imprint dropdown (coin_1_side Side Two)
  // ----------------------------------------------------------------

  _onImprintChange(changedSelect) {
    const value = changedSelect.value;

    // Keep both panels' selects in sync
    this.imprintSelects.forEach((s) => { s.value = value; });

    // Update preview image in all Side Two sections
    const imgUrl = value && this.imprintImages ? (this.imprintImages[value] || '') : '';
    this.el.querySelectorAll('.logo-chooser__imprint-preview').forEach((preview) => {
      const img = preview.querySelector('.logo-chooser__imprint-img');
      if (imgUrl) {
        if (img) { img.src = imgUrl; img.alt = value; }
        preview.hidden = false;
      } else {
        preview.hidden = true;
      }
    });

    // Write to cart property hidden input
    if (this.propImprint) this.propImprint.value = value;
  }

  // ----------------------------------------------------------------
  // Existing logo selection
  // ----------------------------------------------------------------

  _onExistingSelected(radio) {
    this.el.querySelectorAll('.logo-chooser__existing-item').forEach((item) => {
      item.classList.remove('logo-chooser__existing-item--selected');
    });
    radio.closest('.logo-chooser__existing-item')?.classList.add('logo-chooser__existing-item--selected');
    if (this.propSelected) this.propSelected.value = radio.value;
    this._updateExistingFeePreview(radio.value);
  }

  async _updateExistingFeePreview(imageUrl) {
    const feeVariantId = this.feeVariants.existing;
    if (!feeVariantId || feeVariantId === '0') return;
    try {
      const cart    = await this._getCart();
      const feeLine = cart.items.find((item) => String(item.variant_id) === String(feeVariantId));
      if (!feeLine) return;
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: feeLine.key,
          quantity: 1,
          properties: { ...feeLine.properties, 'Logo Preview': imageUrl },
        }),
      });
    } catch (err) {
      console.warn('[LogoChooser] Existing logo preview update failed:', err);
    }
  }

  // ----------------------------------------------------------------
  // Error display
  // ----------------------------------------------------------------

  _showError(msg) {
    if (this.errorEl) {
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }
  }

  _clearError() {
    if (this.errorEl) {
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
  }
}

document.querySelectorAll('.logo-chooser').forEach((el) => new LogoChooser(el));
